import { useEffect, useMemo, useRef, useState } from "react";
import { TYPING_TTL_MS } from "@astrum/shared";
import { useAuth } from "../../lib/auth.js";
import { useRealtime, useRealtimeFrames } from "../../lib/realtime-context.js";

/**
 * Who is typing in the open channel, by display name.
 *
 * Also tells the gateway which channel is on screen: typing frames are only
 * sent for the focused channel, so the two are set (and reset) together.
 */
export function useTypingUsers(channelId: string | undefined): string[] {
  const { viewer } = useAuth();
  const { status: realtimeStatus, setFocus } = useRealtime();
  /** userId -> display name, for whoever is typing in the open channel. */
  const [typingUsers, setTypingUsers] = useState<Map<string, string>>(new Map());
  const typingTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  /* ── tell the gateway which channel is on screen ── */
  useEffect(() => {
    setFocus(channelId ?? null);
    setTypingUsers(new Map());
    // Timers from the previous channel would otherwise fire against the new one.
    const timers = typingTimers.current;
    for (const timer of timers.values()) clearTimeout(timer);
    timers.clear();
  }, [channelId, setFocus, realtimeStatus]);

  useEffect(() => {
    const timers = typingTimers.current;
    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  useRealtimeFrames((frame) => {
    if (frame.t === "message") {
      if (frame.message.channelId !== channelId) return;
      // A message from someone means they have stopped typing.
      setTypingUsers((prev) => {
        if (!prev.has(frame.message.authorId)) return prev;
        const next = new Map(prev);
        next.delete(frame.message.authorId);
        return next;
      });
      return;
    }

    if (frame.t !== "typing") return;
    if (frame.channelId !== channelId || frame.userId === viewer?.id) return;
    setTypingUsers((prev) => new Map(prev).set(frame.userId, frame.displayName));

    // Expired client-side on a timer: the server sends no "stopped typing",
    // deliberately, since a client that crashes mid-word would never send it.
    const timers = typingTimers.current;
    clearTimeout(timers.get(frame.userId));
    timers.set(
      frame.userId,
      setTimeout(() => {
        timers.delete(frame.userId);
        setTypingUsers((prev) => {
          const next = new Map(prev);
          next.delete(frame.userId);
          return next;
        });
      }, TYPING_TTL_MS),
    );
  });

  return useMemo(() => [...typingUsers.values()], [typingUsers]);
}
